import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileText, CheckCircle2, XCircle } from "lucide-react";
import * as mammoth from "mammoth";
import { extractCandidatesFromText } from "@/utils/candidateExtractor";

interface WordImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any[]) => void;
}

export function WordImportDialog({ isOpen, onClose, onImport }: WordImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [error, setError] = useState("");

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      if (!selected.name.endsWith('.docx')) {
        setError("Veuillez sélectionner un fichier .docx valide.");
        return;
      }
      setFile(selected);
      setError("");
      await processWordFile(selected);
    }
  };

  const processWordFile = async (selectedFile: File) => {
    setLoading(true);
    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      const text = result.value;
      const candidates = extractCandidatesFromText(text);
      setPreviewData(candidates);
    } catch (err: any) {
      console.error(err);
      setError("Erreur lors de la lecture du fichier Word. Assurez-vous qu'il n'est pas corrompu.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (previewData.length > 0) {
      onImport(previewData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setError("");
    onClose();
  };

  const updateCandidate = (index: number, field: string, value: string) => {
    const updated = [...previewData];
    updated[index][field] = value;
    setPreviewData(updated);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[900px] bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> Import depuis Word
          </DialogTitle>
          <DialogDescription>
            Importez une liste de candidats depuis un document Word (.docx) contenant le texte (en Français ou Arabe).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!file && (
            <div className="border-2 border-dashed border-border rounded-xl p-8 flex flex-col items-center justify-center bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer relative">
              <input
                type="file"
                accept=".docx"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <UploadCloud className="w-10 h-10 text-muted-foreground mb-3" />
              <p className="text-sm font-semibold">Cliquez ou glissez un fichier .docx ici</p>
              <p className="text-xs text-muted-foreground mt-1">Le texte sera analysé intelligemment.</p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              <XCircle className="w-4 h-4" /> {error}
            </div>
          )}

          {loading && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              Analyse du document en cours...
            </div>
          )}

          {file && !loading && previewData.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {previewData.length} candidats détectés
                </span>
                <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPreviewData([]); }} className="text-xs h-7">
                  Changer de fichier
                </Button>
              </div>

              <div className="border border-border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 w-1/4">Nom Complet (FR)</th>
                      <th className="px-3 py-2 w-1/4">Nom Complet (AR)</th>
                      <th className="px-3 py-2">Région / Province</th>
                      <th className="px-3 py-2">Parti</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {previewData.slice(0, 50).map((c, i) => (
                      <tr key={i} className="hover:bg-muted/20">
                        <td className="px-3 py-2 font-medium">
                           <div className="flex gap-1">
                             <input type="text" value={c.firstName} onChange={e => updateCandidate(i, 'firstName', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1" placeholder="Prénom" />
                             <input type="text" value={c.lastName} onChange={e => updateCandidate(i, 'lastName', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1" placeholder="Nom" />
                           </div>
                        </td>
                        <td className="px-3 py-2 font-medium">
                           <div className="flex gap-1">
                             <input type="text" value={c.firstNameAr} onChange={e => updateCandidate(i, 'firstNameAr', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-right" placeholder="الاسم" dir="rtl" />
                             <input type="text" value={c.lastNameAr} onChange={e => updateCandidate(i, 'lastNameAr', e.target.value)} className="w-full bg-background border border-border rounded px-2 py-1 text-right" placeholder="النسب" dir="rtl" />
                           </div>
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{c.region} - {c.province}</td>
                        <td className="px-3 py-2">{c.partyAcronym}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewData.length > 50 && <p className="text-xs text-center text-muted-foreground pt-2">+ {previewData.length - 50} autres candidats</p>}
            </div>
          )}
          
          {file && !loading && previewData.length === 0 && !error && (
            <div className="text-center py-8 text-sm text-muted-foreground border-2 border-dashed border-border rounded-xl">
              Aucun candidat valide détecté dans ce document.
              <br/>
              <Button variant="outline" size="sm" onClick={() => setFile(null)} className="mt-3">Réessayer</Button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={handleClose}>Annuler</Button>
          <Button onClick={handleConfirm} disabled={previewData.length === 0 || loading} className="bg-blue-600 hover:bg-blue-700 text-white">
            Importer les données
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
